/** Download and classify payloads returned by signed evidence source URLs. */

import type { StructuredEvidenceDocumentResponse } from "../../../../api/clinicalTrialsApiTypes";
import { isStructuredEvidenceDocumentResponse } from "./structuredEvidenceUtils";

/** Result of downloading the URL returned by the authenticated source endpoint. */
export type DownloadedEvidence =
  | { kind: "pdf"; blob: Blob }
  | { kind: "structured"; document: StructuredEvidenceDocumentResponse }
  | { kind: "text"; text: string };

/**
 * Download a signed source URL and distinguish PDF, structured JSON, and text.
 */
export async function downloadEvidence(
  url: string,
  signal: AbortSignal,
): Promise<DownloadedEvidence> {
  const response = await fetch(url, { signal });
  const blob = await response.blob();
  const contentType = response.headers.get("content-type") ?? blob.type;
  const prefix = await blob.slice(0, 64).text();
  const normalizedContentType = contentType.toLowerCase();
  const looksLikeJson =
    normalizedContentType.includes("json") || /^\s*[\[{]/.test(prefix);

  if (!response.ok) {
    throw new Error(`Unable to load document (${response.status})`);
  }

  if (looksLikeJson) {
    let payload: unknown;
    try {
      payload = JSON.parse(await blob.text());
    } catch {
      throw new Error("The document source returned invalid JSON");
    }
    if (isStructuredEvidenceDocumentResponse(payload)) {
      return { kind: "structured", document: payload };
    }
  }

  if (looksLikeJson) {
    throw new Error("The document source returned unsupported JSON");
  }

  const looksLikePdf =
    normalizedContentType.includes("pdf") || /^\s*%PDF-/.test(prefix);
  if (looksLikePdf) {
    return { kind: "pdf", blob };
  }

  const text = (await blob.text()).trim();
  if (!text) {
    throw new Error("The document source returned empty text");
  }
  return { kind: "text", text };
}
